import { createContext, useContext, useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { userPrefsApi, type Bookmark } from '@/services/userPrefsApi';
import { docIdHash } from '@/lib/docIdHash';

interface BookmarksContextValue {
  bookmarks: Bookmark[];
  bookmarkedSet: Set<string>;
  loading: boolean;
  add: (bookmark: { docId: string; title: string; repoFullName: string; path: string; docType: string; folderId?: string }) => Promise<Bookmark>;
  remove: (docIdHash: string) => Promise<void>;
  update: (docIdHash: string, updates: { folderId?: string | null; note?: string | null }) => Promise<Bookmark>;
  refresh: () => Promise<void>;
  /** Compute the hash for a docId (async, uses SubtleCrypto). */
  computeHash: (docId: string) => Promise<string>;
}

const BookmarksContext = createContext<BookmarksContextValue | null>(null);

export function BookmarksProvider({ children }: { children: ReactNode }) {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const items = await userPrefsApi.listBookmarks();
      setBookmarks(items);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const add = useCallback(async (bookmark: {
    docId: string;
    title: string;
    repoFullName: string;
    path: string;
    docType: string;
    folderId?: string;
  }) => {
    const created = await userPrefsApi.createBookmark(bookmark);
    setBookmarks((prev) => [created, ...prev]);
    return created;
  }, []);

  const remove = useCallback(async (hash: string) => {
    setBookmarks((prev) => prev.filter((b) => b.docIdHash !== hash));
    try {
      await userPrefsApi.deleteBookmark(hash);
    } catch {
      fetch();
    }
  }, [fetch]);

  const update = useCallback(async (hash: string, updates: { folderId?: string | null; note?: string | null }) => {
    const updated = await userPrefsApi.updateBookmark(hash, updates);
    setBookmarks((prev) => prev.map((b) => b.docIdHash === hash ? updated : b));
    return updated;
  }, []);

  const bookmarkedSet = useMemo(
    () => new Set(bookmarks.map((b) => b.docIdHash)),
    [bookmarks],
  );

  const value = useMemo(() => ({
    bookmarks,
    bookmarkedSet,
    loading,
    add,
    remove,
    update,
    refresh: fetch,
    computeHash: docIdHash,
  }), [bookmarks, bookmarkedSet, loading, add, remove, update, fetch]);

  return (
    <BookmarksContext.Provider value={value}>
      {children}
    </BookmarksContext.Provider>
  );
}

export function useBookmarksContext() {
  const ctx = useContext(BookmarksContext);
  if (!ctx) throw new Error('useBookmarksContext must be used within BookmarksProvider');
  return ctx;
}
